/** @jsxImportSource react */
import { abbreviatePrice } from '@common/helpers/abbreviatePrice';
import BarChart from '@react/data-hub/components/BarChart';
import NoData from '@react/data-hub/components/NoData';
import { useDatahub } from '@react/data-hub/hooks/useDatahub';
import { ChartOptions } from 'chart.js';

export const options: ChartOptions = {
  responsive: true,
  aspectRatio: 3,
  plugins: {
    datalabels: {
      color: 'white',
      formatter: abbreviatePrice,
      labels: {
        title: {
          align: 'bottom',
        },
      },
    },
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false, // Hide grid lines on the x-axis
      },
    },
    y: {
      border: {
        display: false,
      },
      position: 'right', // Display y-axis labels on the right-hand side
      ticks: {
        stepSize: 2000,
        callback: (value) => abbreviatePrice(Number(value)),
      },
    },
  },
};

const getFormatPricingDataForChart = (
  data: Array<{ price: number; label: string }>
) => {
  return {
    labels: data.map(({ label }) => label),
    datasets: [
      {
        label: 'Montly average cost',
        data: data.map(({ price }) => price),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(26, 116, 115, 1)',
        categoryPercentage: 0.5,
      },
    ],
  };
};

const fallbackData = {
  nationalAveragePricing: 4623,
  cityAveragePricing: 5527,
  countyAveragePricing: 5527,
  stateAveragePricing: 5175,
};

export default function PricingBarChart({
  shouldRenderFallback = true,
}: {
  shouldRenderFallback?: boolean;
}) {
  const { searchInsights, locationString } = useDatahub();

  const {
    cityAveragePricing,
    countyAveragePricing,
    nationalAveragePricing,
    stateAveragePricing,
    countyName,
    stateName,
  } = searchInsights || {};

  const noPricingData =
    !cityAveragePricing &&
    !countyAveragePricing &&
    !stateAveragePricing &&
    !nationalAveragePricing;

  const pricingData = !noPricingData
    ? [
        {
          price: Number(cityAveragePricing),
          label: locationString,
        },
        {
          price: Number(countyAveragePricing),
          label: `${countyName ? countyName : ''} County`,
        },
        {
          price: Number(stateAveragePricing),
          label: stateName ? stateName : 'State',
        },
        {
          price: Number(nationalAveragePricing),
          label: 'National',
        },
      ].filter(({ price }) => price > 0)
    : [
        {
          price: fallbackData.cityAveragePricing,
          label: 'San Francisco, CA',
        },
        {
          price: fallbackData.countyAveragePricing,
          label: 'County',
        },
        {
          price: fallbackData.stateAveragePricing,
          label: 'State',
        },
        {
          price: fallbackData.nationalAveragePricing,
          label: 'National',
        },
      ];

  const data = getFormatPricingDataForChart(pricingData);

  if (noPricingData && !shouldRenderFallback) {
    return null;
  }

  return (
    <NoData noData={noPricingData}>
      {(!noPricingData || shouldRenderFallback) && (
        <BarChart options={options} data={data} />
      )}
    </NoData>
  );
}
